import { useState, useEffect, useRef } from "react";
import { StickerPickerInline, StickerMessage } from "../StickerPicker";
import { useApp, api } from "../context";
import { Shell, EmptyState, TierBadge } from "../components";

// ── MESSAGES LIST PAGE ──
export const MessagesPage = () => {
  const { c, dark, go, user, conversations, loadConversations, setActiveConvo, loadMessages } = useApp();
  const [search, setSearch] = useState("");

  useEffect(() => { if (user) loadConversations(); }, [user?.id]); // eslint-disable-line
  useEffect(() => {
    if (!user) return;
    const i = setInterval(() => loadConversations(), 8000);
    return () => clearInterval(i);
  }, [user?.id]); // eslint-disable-line

  const openChat = async (cv) => { setActiveConvo(cv); await loadMessages(cv.id); go("chat"); };
  const filtered = conversations.filter(cv => cv.participantName?.toLowerCase().includes(search.toLowerCase()));

  return (
    <Shell title="Messages">
      <div style={{ padding: "12px 0" }}>
        <div style={{ background: dark ? "#161720" : "#f4f5f9", borderRadius: 14, border: `1px solid ${dark ? "rgba(255,255,255,0.07)" : "#e5e7eb"}`, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={dark ? "#8890b0" : "#9ca3af"} strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search conversations..." style={{ flex: 1, border: "none", background: "transparent", color: dark ? "#f0f0fa" : "#1a1c2e", fontSize: 13, outline: "none", fontFamily: "inherit" }} />
        </div>
        {filtered.length === 0 && search === "" ? (
          <EmptyState icon="✉️" title="No messages yet" desc="Start a conversation from someone's profile!" />
        ) : filtered.length === 0 ? (
          <EmptyState icon="🔍" title="No results" desc={`No conversations matching "${search}"`} />
        ) : filtered.map(cv => (
          <div key={cv.id} onClick={() => openChat(cv)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 14px", borderRadius: 16, cursor: "pointer", background: dark ? "#161720" : "#fff", border: `1px solid ${cv.unread > 0 ? "rgba(108,92,231,0.3)" : (dark ? "rgba(255,255,255,0.07)" : "#e5e7eb")}`, marginBottom: 8 }}>
            <div style={{ position: "relative", flexShrink: 0 }}>
              <div style={{ width: 50, height: 50, borderRadius: 16, background: cv.participantPic ? `url(${cv.participantPic}) center/cover` : "linear-gradient(135deg, #6c5ce7, #e91e8c)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, overflow: "hidden" }}>{!cv.participantPic && cv.participantEmoji}</div>
              {cv.partnerOnline && <div style={{ position: "absolute", bottom: -1, right: -1, width: 13, height: 13, borderRadius: 7, background: "#10b981", border: `2px solid ${dark ? "#0b0c10" : "#fff"}` }} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: dark ? "#f0f0fa" : "#1a1c2e" }}>{cv.participantName}</span>
                  <TierBadge plan={cv.participantPlan} verified={cv.participantVerified} size={13} />
                </div>
                <span style={{ fontSize: 11, color: cv.unread > 0 ? "#6c5ce7" : (dark ? "#8890b0" : "#9ca3af"), fontWeight: cv.unread > 0 ? 700 : 400 }}>{cv.lastTime}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: 13, color: cv.unread > 0 ? (dark ? "#f0f0fa" : "#1a1c2e") : (dark ? "#8890b0" : "#9ca3af"), fontWeight: cv.unread > 0 ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{cv.lastMessage || "No messages yet"}</div>
                {cv.unread > 0 && <span style={{ minWidth: 20, height: 20, borderRadius: 10, background: "#6c5ce7", color: "#fff", fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 5px", flexShrink: 0, marginLeft: 8 }}>{cv.unread}</span>}
              </div>
            </div>
          </div>
        ))}
        <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "16px 0 12px" }}>
          <div style={{ flex: 1, height: 1, background: dark ? "rgba(255,255,255,0.07)" : "#e5e7eb" }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: dark ? "#8890b0" : "#9ca3af", letterSpacing: "0.06em" }}>GROUPS</span>
          <div style={{ flex: 1, height: 1, background: dark ? "rgba(255,255,255,0.07)" : "#e5e7eb" }} />
        </div>
        <div style={{ background: dark ? "#161720" : "#fff", borderRadius: 16, border: `1px solid ${dark ? "rgba(255,255,255,0.07)" : "#e5e7eb"}`, padding: "20px 16px", textAlign: "center" }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>👥</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: dark ? "#f0f0fa" : "#1a1c2e", marginBottom: 4 }}>Group Chats Coming Soon</div>
          <div style={{ fontSize: 12, color: dark ? "#8890b0" : "#9ca3af", lineHeight: 1.5 }}>Premium & Elite members get access first!</div>
        </div>
      </div>
    </Shell>
  );
};

// ── CHAT PAGE ──
export const ChatPage = () => {
  const { c, dark, go, user, activeConvo, setActiveConvo, conversations, setConversations, loadMessages, loadConversations, setViewingUser, flash } = useApp();
  const [msgInput, setMsgInput] = useState("");
  const [showSticker, setShowSticker] = useState(false);
  const [showMedia, setShowMedia] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const msgEndRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);

  const convo = conversations.find(cv => cv.id === activeConvo?.id) || activeConvo;
  const messages = convo?.messages || [];
  const partnerName = convo?.participantName || "Unknown";
  const partnerEmoji = convo?.participantEmoji || "👤";
  const partnerPic = convo?.participantPic || null;
  const partnerPlan = convo?.participantPlan || "free";
  const partnerVerified = convo?.participantVerified || false;

  // ── Gradient bg matching approved preview ──
  const chatBg = dark ? "#0d0e16" : "#f0eef8";
  const chatBgImage = dark
    ? "radial-gradient(ellipse at top left, rgba(108,92,231,0.08) 0%, transparent 60%), radial-gradient(ellipse at bottom right, rgba(236,72,153,0.06) 0%, transparent 60%)"
    : "radial-gradient(ellipse at top left, rgba(108,92,231,0.06) 0%, transparent 60%), radial-gradient(ellipse at bottom right, rgba(236,72,153,0.05) 0%, transparent 60%)";

  const scrollToBottom = (smooth) => { setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: smooth ? "smooth" : "auto" }), 100); };

  useEffect(() => {
    scrollToBottom(false);
    // Auto-send Jilink intro message
    const autoSend = sessionStorage.getItem("gc_jilink_autosend");
    if (autoSend && activeConvo?.id) {
      sessionStorage.removeItem("gc_jilink_autosend");
      setTimeout(async () => {
        const res = await api(`/conversations/${activeConvo.id}/messages`, {
          method: "POST",
          body: JSON.stringify({ content: autoSend })
        });
        if (res.ok) { loadMessages(activeConvo.id); flash("Intro message sent! 💜"); }
      }, 1000);
    }
  }, [activeConvo?.id]); // eslint-disable-line

  const prevMsgCount = useRef(0);
  useEffect(() => {
    if (messages.length !== prevMsgCount.current) {
      scrollToBottom(messages.length > prevMsgCount.current);
      prevMsgCount.current = messages.length;
    }
  }, [messages.length]);

  useEffect(() => {
    if (!activeConvo?.id) return;
    let lastMsgId = null;
    const poll = async () => {
      const res = await api(`/conversations/${activeConvo.id}`);
      if (!res.ok) return;
      const raw = Array.isArray(res.data?.data) ? res.data.data : [];
      if (raw.length === 0) return;
      const uid = user?.id || localStorage.getItem("gc_uid");
      const mapped = raw.map(m => ({
        id: m.id,
        from: String(m.user_id) === String(uid) ? "me" : String(m.user_id),
        fromName: m.user?.name || "Unknown",
        text: m.content,
        sticker: m.sticker ? { url: m.sticker.url, name: m.sticker.name } : null,
        time: m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "Just now",
      }));
      // Only update if something actually changed
      const newLastId = mapped[mapped.length - 1]?.id;
      if (newLastId === lastMsgId) return;
      lastMsgId = newLastId;
      setActiveConvo(prev => prev ? { ...prev, messages: mapped } : prev);
      setConversations(prev => prev.map(cv =>
        cv.id === activeConvo.id
          ? { ...cv, messages: mapped, unread: 0 }
          : cv
      ));
    };
    poll();
    const i = setInterval(poll, 5000);
    return () => clearInterval(i);
  }, [activeConvo?.id]); // eslint-disable-line

  useEffect(() => {
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const openTray = (tray) => {
    if (tray === "sticker") { setShowSticker(p => !p); setShowMedia(false); }
    if (tray === "media")   { setShowMedia(p => !p);   setShowSticker(false); }
    setShowMenu(false);
    setTimeout(() => scrollToBottom(false), 120);
  };

  const sendMsg = async (stickerObj = null) => {
    if ((!msgInput.trim() && !stickerObj) || !activeConvo || sending) return;
    const txt = msgInput.trim();
    if (!stickerObj) setMsgInput("");
    setShowSticker(false);
    setShowMedia(false);
    setSending(true);
    const tempId = `temp_${Date.now()}`;
    const tempMsg = { id: tempId, from: "me", text: stickerObj ? null : txt, sticker: stickerObj ? { url: stickerObj.url, name: stickerObj.name } : null, time: "Just now", sending: true };
    setActiveConvo(prev => ({ ...prev, messages: [...(prev?.messages || []), tempMsg] }));
    setConversations(prev => prev.map(cv => cv.id === activeConvo.id ? { ...cv, messages: [...(cv.messages || []), tempMsg], lastMessage: stickerObj ? "🎭 Sticker" : txt, lastTime: "Just now" } : cv));
    const res = await api(`/conversations/${activeConvo.id}/messages`, { method: "POST", body: JSON.stringify({ content: txt || null, sticker_id: stickerObj ? stickerObj.id : null }) });
    setSending(false);
    if (res.ok) {
      setActiveConvo(prev => ({ ...prev, messages: (prev?.messages || []).filter(m => m.id !== tempId) }));
      loadMessages(activeConvo.id);
    } else {
      flash("Failed to send", "error");
      setActiveConvo(prev => ({ ...prev, messages: (prev?.messages || []).filter(m => m.id !== tempId) }));
    }
    inputRef.current?.focus();
  };

  const doBlock = async () => {
    const res = await api(`/profile/${activeConvo.participantId}/block`, { method: "POST" });
    setShowBlockConfirm(false);
    if (res.ok) {
      // Remove blocked user from conversations list immediately
      setConversations(prev => prev.filter(cv => cv.participantId !== activeConvo.participantId));
      flash(`${partnerName} has been blocked`, "error");
      goBack();
    } else flash("Failed to block user", "error");
  };

  const doDelete = async () => {
    const res = await api(`/conversations/${activeConvo.id}`, { method: "DELETE" });
    setShowDeleteConfirm(false);
    if (res.ok) {
      setConversations(prev => prev.filter(cv => cv.id !== activeConvo.id));
      flash("Chat deleted");
      goBack();
    } else flash("Failed to delete chat", "error");
  };

  const goBack = () => { loadConversations(); go("messages"); };
  if (!convo) return null;

  // ── 3-DOT MENU ──
  const DotMenu = () => (
    <div ref={menuRef} style={{ position: "absolute", top: "100%", right: 0, marginTop: 6, background: dark ? "#1c1d2a" : "#fff", border: `1px solid ${dark ? "rgba(255,255,255,0.11)" : "#e5e7eb"}`, borderRadius: 14, padding: 6, zIndex: 300, minWidth: 185, boxShadow: "0 8px 32px rgba(0,0,0,0.5)", animation: "menuPop 0.15s cubic-bezier(0.34,1.56,0.64,1)" }}>
      <style>{`@keyframes menuPop { from { transform: scale(0.85) translateY(-6px); opacity:0; } to { transform: scale(1); opacity:1; } }`}</style>
      {[
        { icon: "👤", label: "View Profile",        bg: "#6c5ce720", action: () => { setViewingUser(activeConvo.participantId); go("user_profile"); } },
        { icon: "💰", label: "Make an Offer",       bg: "#f59e0b20", action: () => flash("Offers coming soon! 🚀") },
        { icon: "📸", label: "Request Photo",       bg: "#10b98120", action: () => flash("Photo requests coming soon! 🚀") },
        { icon: "🔔", label: "Mute Notifications", bg: "#3b82f620", action: () => flash("Mute notifications coming soon! 🔔") },
      ].map((item, i) => (
        <button key={i} onClick={() => { item.action(); setShowMenu(false); }} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 12px", borderRadius: 10, border: "none", background: "transparent", cursor: "pointer" }}>
          <div style={{ width: 30, height: 30, borderRadius: 9, background: item.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>{item.icon}</div>
          <span style={{ fontSize: 13, fontWeight: 600, color: dark ? "#f0f0fa" : "#1a1c2e", whiteSpace: "nowrap" }}>{item.label}</span>
        </button>
      ))}
      <div style={{ height: 1, background: dark ? "rgba(255,255,255,0.07)" : "#e5e7eb", margin: "4px 0" }} />
      {[
        { icon: "🚫", label: "Block User",   bg: "#ef444420", action: () => { setShowBlockConfirm(true); setShowMenu(false); }},
        { icon: "🗑️", label: "Delete Chat",  bg: "#ef444420", action: () => { setShowDeleteConfirm(true); setShowMenu(false); }},
      ].map((item, i) => (
        <button key={i} onClick={() => { item.action(); setShowMenu(false); }} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 12px", borderRadius: 10, border: "none", background: "transparent", cursor: "pointer" }}>
          <div style={{ width: 30, height: 30, borderRadius: 9, background: item.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>{item.icon}</div>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#ef4444" }}>{item.label}</span>
        </button>
      ))}
    </div>
  );

  // ── MEDIA TRAY (opens below input, inline — like sticker in post comments) ──
  const MediaTray = () => (
    <div style={{ background: dark ? "#12131a" : "#fff", borderTop: `1px solid ${dark ? "rgba(255,255,255,0.07)" : "#e5e7eb"}`, padding: "14px 16px", flexShrink: 0 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
        {[
          { icon: "📷", label: "Photo",     action: () => { flash("Photo sharing coming soon!"); setShowMedia(false); } },
          { icon: "🎬", label: "Video",     action: () => { flash("Video sharing coming soon!"); setShowMedia(false); } },
          { icon: "📎", label: "File",      action: () => { flash("File sharing coming soon!"); setShowMedia(false); } },
          { icon: "📍", label: "Location",  action: () => { flash("Location sharing coming soon!"); setShowMedia(false); } },
          { icon: "🎤", label: "Voice",     action: () => { flash("Voice notes coming soon!"); setShowMedia(false); } },
          { icon: "🎵", label: "Audio",     action: () => { flash("Audio sharing coming soon!"); setShowMedia(false); } },
          { icon: "👤", label: "Contact",   action: () => { flash("Contact sharing coming soon!"); setShowMedia(false); } },
          { icon: "🎭", label: "GIF",       action: () => { flash("GIF sharing coming soon!"); setShowMedia(false); } },
        ].map((item, i) => (
          <button key={i} onClick={item.action} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "12px 4px", borderRadius: 14, border: `1px solid ${dark ? "rgba(255,255,255,0.07)" : "#e5e7eb"}`, background: dark ? "#161720" : "#f4f5f9", cursor: "pointer" }}>
            <span style={{ fontSize: 26 }}>{item.icon}</span>
            <span style={{ fontSize: 10, fontWeight: 700, color: dark ? "#8890b0" : "#6b7094" }}>{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );

  // ── CONFIRM MODALS ──
  const ConfirmModal = ({ show, icon, title, desc, confirmLabel, confirmColor, onConfirm, onCancel }) => {
    if (!show) return null;
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
        <div style={{ background: dark ? "#1c1d2a" : "#fff", borderRadius: 20, padding: "28px 24px", maxWidth: 340, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.5)", animation: "menuPop 0.2s cubic-bezier(0.34,1.56,0.64,1)" }}>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <div style={{ fontSize: 44, marginBottom: 12 }}>{icon}</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: dark ? "#f0f0fa" : "#1a1c2e", marginBottom: 8 }}>{title}</div>
            <div style={{ fontSize: 13, color: dark ? "#8890b0" : "#6b7094", lineHeight: 1.6 }}>{desc}</div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={onCancel} style={{ flex: 1, padding: "12px", borderRadius: 12, border: `1px solid ${dark ? "rgba(255,255,255,0.1)" : "#e5e7eb"}`, background: "transparent", color: dark ? "#8890b0" : "#6b7094", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
            <button onClick={onConfirm} style={{ flex: 1, padding: "12px", borderRadius: 12, border: "none", background: confirmColor, color: "#fff", fontSize: 14, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>{confirmLabel}</button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ height: "100dvh", display: "flex", flexDirection: "column", width: "100%", maxWidth: 480, margin: "0 auto", fontFamily: "'DM Sans',sans-serif", overflow: "hidden", position: "fixed", top: 0, left: 0, right: 0, bottom: 0 }}>

      <ConfirmModal
        show={showBlockConfirm}
        icon="🚫"
        title={`Block ${partnerName}?`}
        desc={`They won't be able to message you, see your profile, or find you anywhere on the platform.`}
        confirmLabel="Yes, Block"
        confirmColor="#ef4444"
        onConfirm={doBlock}
        onCancel={() => setShowBlockConfirm(false)}
      />

      <ConfirmModal
        show={showDeleteConfirm}
        icon="🗑️"
        title="Delete this chat?"
        desc="All messages will be permanently deleted. This cannot be undone."
        confirmLabel="Yes, Delete"
        confirmColor="#ef4444"
        onConfirm={doDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      {/* ── TOP BAR — matches approved preview ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: dark ? "#12131a" : "#fff", borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.07)" : "#e5e7eb"}`, flexShrink: 0, zIndex: 10 }}>

        <button onClick={goBack} style={{ width: 34, height: 34, borderRadius: 10, border: `1px solid ${dark ? "rgba(255,255,255,0.11)" : "#e5e7eb"}`, background: dark ? "#161720" : "#f4f5f9", color: dark ? "#8890b0" : "#6b7094", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 16, flexShrink: 0 }}>←</button>

        <div style={{ position: "relative", flexShrink: 0, cursor: "pointer" }} onClick={() => { setViewingUser(activeConvo.participantId); go("user_profile"); }}>
          <div style={{ width: 42, height: 42, borderRadius: 13, background: partnerPic ? `url(${partnerPic}) center/cover` : "linear-gradient(135deg, #e91e8c, #6c5ce7)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, overflow: "hidden", border: `2px solid ${dark ? "rgba(255,255,255,0.1)" : "#e5e7eb"}` }}>{!partnerPic && partnerEmoji}</div>
          {convo?.partnerOnline && <div style={{ position: "absolute", bottom: -1, right: -1, width: 11, height: 11, borderRadius: 6, background: "#10b981", border: `2px solid ${dark ? "#12131a" : "#fff"}` }} />}
        </div>

        <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={() => { setViewingUser(activeConvo.participantId); go("user_profile"); }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: dark ? "#f0f0fa" : "#1a1c2e" }}>{partnerName}</span>
            <TierBadge plan={partnerPlan} verified={partnerVerified} size={13} />
          </div>
          <div style={{ fontSize: 11, fontWeight: 600, color: convo?.partnerOnline ? "#10b981" : (dark ? "#8890b0" : "#9ca3af"), display: "flex", alignItems: "center", gap: 4, marginTop: 1 }}>
            {convo?.partnerOnline && <div style={{ width: 6, height: 6, borderRadius: 3, background: "#10b981" }} />}
            {convo?.partnerOnline ? "Active now" : "Offline"}
          </div>
        </div>

        <div style={{ position: "relative" }}>
          <button onClick={() => { setShowMenu(p => !p); setShowSticker(false); setShowMedia(false); }} style={{ width: 34, height: 34, borderRadius: 10, border: `1px solid ${dark ? "rgba(255,255,255,0.11)" : "#e5e7eb"}`, background: showMenu ? "#6c5ce720" : (dark ? "#161720" : "#f4f5f9"), color: dark ? "#8890b0" : "#6b7094", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 18, flexShrink: 0 }}>⋯</button>
          {showMenu && <DotMenu />}
        </div>
      </div>

      {/* ── MESSAGES AREA — gradient bg from approved preview ── */}
      <div style={{ flex: 1, overflowY: "auto", minHeight: 0, background: chatBg, backgroundImage: chatBgImage, WebkitOverflowScrolling: "touch", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "12px 12px 4px", flex: 1, overflowX: "hidden" }}>

          <div style={{ textAlign: "center", marginBottom: 12 }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: dark ? "#8890b0" : "#9ca3af", background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)", padding: "5px 14px", borderRadius: 12 }}>Today</span>
          </div>

          {!partnerVerified && !user?.verified && (
            <div style={{ background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.25)", borderRadius:12, padding:"10px 14px", marginBottom:12, display:"flex", alignItems:"flex-start", gap:8 }}>
              <span style={{ fontSize:16, flexShrink:0 }}>⚠️</span>
              <div>
                <div style={{ fontSize:12, fontWeight:700, color:"#ef4444", marginBottom:2 }}>Unverified Conversation</div>
                <div style={{ fontSize:11, color:"#ef4444", opacity:0.85, lineHeight:1.5 }}>
                  Neither you nor this user is verified. Be cautious — identities are unconfirmed.{" "}
                  <span onClick={() => go("settings")} style={{ textDecoration:"underline", cursor:"pointer", fontWeight:700 }}>Verify now</span> for safer connections.
                </div>
              </div>
            </div>
          )}

          {messages.length === 0 && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 20px" }}>
              <div style={{ width: 70, height: 70, borderRadius: 22, background: partnerPic ? `url(${partnerPic}) center/cover` : "linear-gradient(135deg, #e91e8c, #6c5ce7)", marginBottom: 14, fontSize: 32, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>{!partnerPic && partnerEmoji}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: dark ? "#f0f0fa" : "#1a1c2e", marginBottom: 4 }}>{partnerName}</div>
              <TierBadge plan={partnerPlan} verified={partnerVerified} size={14} />
              <div style={{ fontSize: 13, color: dark ? "#8890b0" : "#9ca3af", marginTop: 10, textAlign: "center" }}>Say hello and start the conversation! 👋</div>
            </div>
          )}

          {messages.map((msg, i) => {
            const isMine = msg.from === "me";
            const prev = i > 0 ? messages[i - 1] : null;
            const showTimeSep = prev && msg.time !== prev.time && msg.time !== "Just now";
            const nextMsg = i < messages.length - 1 ? messages[i + 1] : null;
            const isLastInGroup = !nextMsg || nextMsg.from !== msg.from;
            const tickColor = msg.sending ? "#999" : (msg.read_at ? "#60a5fa" : "rgba(255,255,255,0.5)");
            return (
              <div key={msg.id}>
                {showTimeSep && (
                  <div style={{ textAlign: "center", padding: "10px 0 6px" }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: dark ? "#8890b0" : "#9ca3af", background: dark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)", padding: "4px 12px", borderRadius: 10 }}>{msg.time}</span>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: isMine ? "flex-end" : "flex-start", marginBottom: isLastInGroup ? 8 : 2, paddingLeft: isMine ? 40 : 0, paddingRight: isMine ? 0 : 40 }}>
                  <div style={{
                    maxWidth: "100%",
                    padding: msg.sticker ? "2px" : "9px 13px",
                    borderRadius: isMine ? `18px 18px ${isLastInGroup ? "4px" : "18px"} 18px` : `18px 18px 18px ${isLastInGroup ? "4px" : "18px"}`,
                    background: msg.sticker ? "transparent" : (isMine ? "linear-gradient(135deg, #6c5ce7 0%, #a855f7 100%)" : (dark ? "#1e2035" : "#ffffff")),
                    border: msg.sticker ? "none" : (isMine ? "none" : `1px solid ${dark ? "#2a2d4a" : "#e8e2f0"}`),
                    color: isMine ? "#fff" : (dark ? "#f0f0fa" : "#1a1c2e"),
                    boxShadow: msg.sticker ? "none" : (isMine ? "0 3px 12px rgba(108,92,231,0.3)" : (dark ? "0 1px 6px rgba(0,0,0,0.3)" : "0 1px 6px rgba(0,0,0,0.08)")),
                    opacity: msg.sending ? 0.75 : 1,
                    wordBreak: "break-word", whiteSpace: "pre-wrap",
                    transition: "opacity 0.2s",
                  }}>
                    {msg.sticker || msg.sticker_url
                      ? <StickerMessage sticker={msg.sticker || { url: msg.sticker_url, name: "sticker" }} darkMode={!!dark} />
                      : <div style={{ fontSize: 14, lineHeight: 1.6 }}>{msg.text}</div>
                    }
                    {!msg.sticker && (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 3, marginTop: 3 }}>
                        <span style={{ fontSize: 10, opacity: 0.55 }}>{msg.time}</span>
                        {isMine && <span style={{ fontSize: 11, color: tickColor }}>{msg.sending ? "⏳" : "✓✓"}</span>}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div ref={msgEndRef} style={{ height: 1, marginBottom: 8 }} />
      </div>

      {/* ══ TRAY AREA — flex siblings BETWEEN messages and input bar ══ */}
      {/* Sticker tray — exactly like post comment sticker (StickerPickerInline, opens below) */}
      {showSticker && (
        <div style={{ flexShrink: 0, background: dark ? "#13141c" : "#fff", borderTop: `1px solid ${dark ? "rgba(255,255,255,0.07)" : "#e5e7eb"}`, maxHeight: 260, overflowY: "auto" }}>
          <StickerPickerInline
            darkMode={!!dark}
            onClose={() => setShowSticker(false)}
            onSelect={s => { sendMsg(s); }}
          />
        </div>
      )}

      {/* Media tray — opens below, grid of options including Mic */}
      {showMedia && <MediaTray />}

{/* ── INPUT BAR ── */}
      <div style={{ background: dark ? "#13141c" : "#fff", borderTop: `1px solid ${dark ? "rgba(255,255,255,0.07)" : "#e5e7eb"}`, padding: "8px 10px", paddingBottom: "max(8px, env(safe-area-inset-bottom))", flexShrink: 0, boxSizing: "border-box" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>

          {/* 📎 Media button — FIRST (left) */}
          <button
            onClick={() => openTray("media")}
            style={{ width: 36, height: 36, minWidth: 36, borderRadius: 18, border: `1px solid ${dark ? "rgba(255,255,255,0.11)" : "#e5e7eb"}`, background: showMedia ? "#6c5ce730" : (dark ? "#1e2035" : "#f0eef8"), cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "background 0.15s" }}
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={showMedia ? "#6c5ce7" : (dark ? "#8890b0" : "#9ca3af")} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
            </svg>
          </button>

          {/* 🎭 Sticker button — SECOND */}
          <button
            onClick={() => openTray("sticker")}
            style={{ width: 36, height: 36, minWidth: 36, borderRadius: 18, border: `1px solid ${dark ? "rgba(255,255,255,0.11)" : "#e5e7eb"}`, background: showSticker ? "#6c5ce730" : (dark ? "#1e2035" : "#f0eef8"), fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "background 0.15s" }}
          >🎭</button>

          {/* Input field */}
          <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", background: dark ? "#1e2035" : "#f0eef8", borderRadius: 22, border: `1px solid ${dark ? "#2a2d4a" : "#d8d0f0"}`, padding: "0 12px", height: 40, gap: 8 }}>
            <input
              ref={inputRef}
              style={{ flex: 1, minWidth: 0, padding: "8px 0", border: "none", background: "transparent", color: dark ? "#f0f0fa" : "#1a1c2e", fontSize: 14, outline: "none", fontFamily: "inherit", width: "100%" }}
              placeholder={`Message ${partnerName}...`}
              value={msgInput}
              onChange={e => setMsgInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMsg()}
              autoComplete="off"
            />
          </div>

          {/* ➤ Send button */}
          <button
            onClick={() => sendMsg()}
            disabled={!msgInput.trim() || sending}
            style={{ width: 38, height: 38, minWidth: 38, borderRadius: 19, border: "none", background: msgInput.trim() ? "linear-gradient(135deg, #6c5ce7, #a855f7)" : (dark ? "#1e2035" : "#e8e2f0"), color: msgInput.trim() ? "#fff" : (dark ? "#8890b0" : "#9ca3af"), fontSize: 16, cursor: msgInput.trim() ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: msgInput.trim() ? "0 4px 14px rgba(108,92,231,0.35)" : "none", transition: "all 0.15s" }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
              <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};